import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { getAuthFromStorage, setAuthToStorage, clearAuthStorage } from './auth-storage'

//const API_BASE = 'http://localhost:4000'
const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:4000/api'

// A small in-memory lock to avoid parallel refreshes
let isRefreshing = false
let refreshPromise: Promise<void> | null = null
let pendingRequests: Array<(err?: any) => void> = []

function delay(ms: number) {
  return new Promise((res) => setTimeout(res, ms))
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const auth = getAuthFromStorage()
  if (auth?.accessToken) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${auth.accessToken}`
  }
  return config
})

// exponential backoff refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean }
    if (!original || original._retry) return Promise.reject(error)

    if (error.response?.status === 401) {
      // start refresh if not already started
      if (!isRefreshing) {
        isRefreshing = true
        refreshPromise = (async () => {
          try {
            const storedAuth = getAuthFromStorage()
            if (!storedAuth?.refreshToken) throw new Error('no refresh token')

            // exponential backoff: try up to 3 times
            let attempts = 0
            let backoff = 500
            while (attempts < 3) {
              try {
                const resp = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken: storedAuth.refreshToken })
                const data = resp.data
                const expiresAt = Date.now() + (data.expiresIn ?? 900) * 1000
                const newAuth = {
                  user: data.user ?? storedAuth.user,
                  accessToken: data.accessToken,
                  refreshToken: data.refreshToken ?? storedAuth.refreshToken,
                  expiresAt,
                }
                setAuthToStorage(newAuth)
                return
              } catch (err) {
                attempts += 1
                await delay(backoff + Math.floor(Math.random() * 100))
                backoff *= 2
              }
            }
            throw new Error('refresh failed')
          } finally {
            isRefreshing = false
          }
        })()
      }

      try {
        await refreshPromise
  // replay pending requests
  pendingRequests.forEach((cb) => cb())
        pendingRequests = []

        // retry original request with new token
        original._retry = true
        if (original.headers) {
          original.headers.Authorization = `Bearer ${getAuthFromStorage()?.accessToken}`
        } else {
          original.headers = { Authorization: `Bearer ${getAuthFromStorage()?.accessToken}` }
        }
        return api(original)
      } catch (err) {
        pendingRequests.forEach((cb) => cb(err))
        pendingRequests = []
        clearAuthStorage()
        return Promise.reject(err)
      }
    }

    return Promise.reject(error)
  }
)

export function queueWhileRefreshing(): Promise<void> {
  return new Promise((resolve, reject) => {
    pendingRequests.push((err) => {
      if (err) reject(err)
      else resolve()
    })
  })
}
